/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import type { VNode } from "preact";
import { useState } from "preact/hooks";
import { Header } from "../ui/Header.js";
import { Button } from "../ui/Button.js";
import { ApiErrorBanner } from "../ui/ApiErrorBanner.js";
import { PasswordInput } from "../ui/PasswordInput.js";
import { useTranslation } from "../context/translation.js";

export function AdminAccountCredentialsScreen({ instanceId, onBack, onReset }: { instanceId: string; onBack: () => void; onReset: (password: string) => Promise<void> }): VNode {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<unknown>();
  const [validation, setValidation] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (event: Event) => {
    event.preventDefault();
    setValidation("");
    setError(undefined);
    if (password.length < 8) { setValidation(t`Password must be at least 8 characters long.`); return; }
    if (password !== confirm) { setValidation(t`Passwords do not match. Please re-type your password.`); return; }
    setPending(true);
    try {
      await onReset(password);
      setPassword("");
      setConfirm("");
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(false);
    }
  };

  return <div class="max-w-2xl space-y-5"><Header title={t`Reset merchant account password`} subtitle={t`Set a new password for merchant account ${instanceId}.`} onBack={onBack} backLabel={t`Merchant account`} /><div class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">{t`The account’s existing password will stop working. Existing login tokens remain governed by the backend’s token policy.`}</div><ApiErrorBanner error={error} title={t`Could not reset password`} />{validation && <div data-error-banner role="alert" class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800">{validation}</div>}<form onSubmit={(event) => void submit(event)} class="space-y-5 rounded-xl border border-gray-200 bg-white p-5"><PasswordInput id="admin-reset-password" label={t`New password`} value={password} onInput={setPassword} autoComplete="new-password" required showRequiredIndicator={false} minLength={8} /><PasswordInput id="admin-reset-confirm" label={t`Confirm new password`} value={confirm} onInput={setConfirm} autoComplete="new-password" required showRequiredIndicator={false} minLength={8} /><div class="flex justify-end gap-3"><Button variant="secondary" onClick={onBack} disabled={pending}>{t`Cancel`}</Button><Button type="submit" isLoading={pending}>{t`Reset password`}</Button></div></form></div>;
}
